<template>
  <v-slide-y-reverse-transition>
    <div v-if="visible" class="gw-action-bar">
      <span class="gw-action-bar__count">{{ countLabel }}</span>

      <button
        type="button"
        class="gw-action-bar__clear"
        @click="$emit('clear')"
      >
        {{ clearText }}
      </button>

      <span v-if="progress" class="gw-action-bar__progress">{{
        progress
      }}</span>

      <!-- Slot : une page peut fournir son propre menu d'options (la page
           video y loge template, statut et « forcer le titre »). Par defaut,
           le menu de la veille globale, en mode sources seulement. -->
      <slot v-if="optionsVisible" name="options">
        <generation-options-menu :site-id="siteId" />
      </slot>

      <rmr-action-button
        :label="primaryLabel"
        :icon="icons.mdiCreation"
        variant="primary"
        size="sm"
        :loading="busy"
        :disabled="busy || primaryDisabled"
        @click="$emit('primary')"
      />

      <!-- Only meaningful when every selected row is a Rappel Conso item. -->
      <rmr-action-menu
        v-if="showRappelConso"
        trigger-mode="icon"
        :trigger-icon="icons.mdiDotsVertical"
        trigger-variant="translucent"
        size="sm"
        :actions="overflowActions"
        @select="$emit('rappel-conso')"
      />
    </div>
  </v-slide-y-reverse-transition>
</template>

<script>
import { computed } from "@vue/composition-api";
import { mdiAlertOctagonOutline, mdiCreation, mdiDotsVertical } from "@mdi/js";

import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrActionMenu from "@/components/ui/actions/RmrActionMenu.vue";
import GenerationOptionsMenu from "./GenerationOptionsMenu.vue";
import { RMR_DANGER } from "@/styles/tokens";

export default {
  name: "GenerationActionBar",
  components: {
    GenerationOptionsMenu,
    RmrActionButton,
    RmrActionMenu,
  },
  props: {
    count: { type: Number, default: 0 },
    mode: {
      type: String,
      default: "sources",
      validator: (value) => ["sources", "angles"].includes(value),
    },
    siteId: { type: Number, default: 0 },
    showRappelConso: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
    progress: { type: String, default: "" },
    primaryDisabled: { type: Boolean, default: false },
    /**
     * `null` = comportement historique (menu en mode sources seulement).
     * Explicite pour une page qui veut ses options aussi en mode angles.
     */
    showOptions: { type: Boolean, default: null },
    /** Libellé du lien secondaire ; par défaut celui du mode. */
    clearLabel: { type: String, default: "" },
    /** Libellé du bouton principal ; par défaut celui du mode. */
    primaryLabel: { type: String, default: "" },
    /** Décompte des éléments sélectionnés ; par défaut celui du mode. */
    countLabel: { type: String, default: "" },
  },
  setup(props) {
    const visible = computed(() => props.count > 0 || props.mode === "angles");

    const clearText = computed(
      () =>
        props.clearLabel ||
        (props.mode === "angles"
          ? "Revenir aux sources"
          : "Tout désélectionner")
    );

    const optionsVisible = computed(() =>
      props.showOptions === null ? props.mode === "sources" : props.showOptions
    );

    const resolvedCountLabel = computed(() => {
      if (props.countLabel) return props.countLabel;
      if (props.mode === "angles") {
        return `${props.count} angle(s) retenu(s)`;
      }
      return `${props.count} article(s) sélectionné(s)`;
    });

    const resolvedPrimaryLabel = computed(() => {
      if (props.primaryLabel) return props.primaryLabel;
      if (props.mode === "angles") {
        return props.count > 1
          ? "Générer les brouillons"
          : "Générer le brouillon";
      }
      return props.count > 1 ? "Préparer les articles" : "Préparer l'article";
    });

    return {
      clearText,
      countLabel: resolvedCountLabel,
      icons: { mdiCreation, mdiDotsVertical },
      optionsVisible,
      overflowActions: [
        {
          key: "rappel-conso",
          label: "Créer un rappel conso multi sources",
          icon: mdiAlertOctagonOutline,
          color: RMR_DANGER,
        },
      ],
      primaryLabel: resolvedPrimaryLabel,
      visible,
    };
  },
};
</script>

<style lang="scss" scoped>
.gw-action-bar {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  // Above .rmr-page-shell__content (5) and the navigation drawer (6).
  z-index: 7;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 12px 10px 22px;
  border-radius: $rmr-radius-pill;
  background: $rmr-text-primary;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.24);
  max-width: calc(100vw - 32px);
}

.gw-action-bar__count {
  color: $rmr-surface-0;
  font-size: 13.5px;
  font-weight: 600;
  white-space: nowrap;
}

.gw-action-bar__clear {
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.65);
  font-family: inherit;
  font-size: 12.5px;
  text-decoration: underline;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    color: $rmr-surface-0;
  }
}

.gw-action-bar__progress {
  color: rgba(255, 255, 255, 0.75);
  font-size: 12.5px;
  white-space: nowrap;
}

@media (max-width: 700px) {
  .gw-action-bar {
    flex-wrap: wrap;
    justify-content: center;
    border-radius: $rmr-radius-2xl;
    padding: 12px 14px;
    gap: 10px;
  }
}
</style>
